import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLeague } from '@/contexts/LeagueContext';
import { getTodayET } from '@/hooks/useAddDrop';

const MIN_REFETCH_MS = 5_000; // minimum 5s between fetches

export interface PlayerMatchScore {
  player_id: string;
  team_id: string;
  game_date: string;
  matchup_week: number;
  trash_points: number;
  anti_points: number;
  total_points: number;
}

/** Aggregated per-player score */
export interface PlayerScoreSummary {
  player_id: string;
  trash_points: number;
  anti_points: number;
  total_points: number;
  game_dates: number; // number of game dates contributing
}

type ScoreMode = 'today' | 'season';

interface UsePlayerMatchScoresParams {
  teamId?: string | null;
  /** Player IDs to include — if provided, scores are filtered client-side to only these players */
  rosterPlayerIds?: string[];
  mode: ScoreMode;
  enabled?: boolean;
}

export function usePlayerMatchScores({
  teamId,
  rosterPlayerIds,
  mode,
  enabled = true,
}: UsePlayerMatchScoresParams) {
  const { activeLeague } = useLeague();
  const leagueId = activeLeague?.id;
  const [scores, setScores] = useState<PlayerMatchScore[]>([]);
  const [loading, setLoading] = useState(false);

  // Deduplication: track the params key of the last fetch + timestamp
  const lastFetchRef = useRef<{ key: string; ts: number }>({ key: '', ts: 0 });
  const fetchingRef = useRef(false);

  // Build a stable param key so we only refetch when inputs actually change
  const paramKey = `${leagueId}|${mode}|${enabled}`;

  const fetchScores = useCallback(async (force = false) => {
    if (!leagueId || !enabled) return;

    const now = Date.now();
    const last = lastFetchRef.current;

    // Skip if same params and within the minimum refetch interval (unless forced)
    if (!force && last.key === paramKey && now - last.ts < MIN_REFETCH_MS) return;
    // Skip if already fetching
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    lastFetchRef.current = { key: paramKey, ts: now };
    setLoading(true);

    // Query by league_id only — do NOT filter by team_id in the DB query.
    // Scores may be written with team_id matching the roster team, OR with
    // team_id=null (unrostered pass). We filter to the right players client-side.
    // Paginate in chunks of 1000 to avoid Supabase row limits / 502s.
    const pageSize = 1000;
    let allRows: PlayerMatchScore[] = [];
    let from = 0;

    while (true) {
      let pageQuery = supabase
        .from('player_match_scores')
        .select('player_id, team_id, game_date, matchup_week, trash_points, anti_points, total_points')
        .eq('league_id', leagueId);

      switch (mode) {
        case 'today': {
          const today = getTodayET();
          pageQuery = pageQuery.eq('game_date', today);
          break;
        }
        case 'season': {
          break;
        }
      }

      const { data, error } = await pageQuery.range(from, from + pageSize - 1);

      if (error) {
        console.error('usePlayerMatchScores error:', error);
        setLoading(false);
        fetchingRef.current = false;
        return;
      }

      allRows = allRows.concat((data || []) as PlayerMatchScore[]);
      if (!data || data.length < pageSize) break;
      from += pageSize;
    }

    setScores(allRows);
    setLoading(false);
    fetchingRef.current = false;
  }, [leagueId, mode, enabled, paramKey]);

  // Only refetch when paramKey actually changes
  useEffect(() => {
    fetchScores();
  }, [paramKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime subscription for live updates (throttled by MIN_REFETCH_MS)
  useEffect(() => {
    if (!leagueId || !enabled) return;

    const channel = supabase
      .channel(`pms-${leagueId}-${mode}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'player_match_scores',
          filter: `league_id=eq.${leagueId}`,
        },
        () => {
          fetchScores();
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [leagueId, mode, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Client-side filter: if rosterPlayerIds provided, only include those players.
  // Only include rostered rows (team_id != null) — unrostered rows are scored for
  // league-wide stats but should not count toward a team's SAB PTS.
  // undefined/not provided = no filter (return all scores)
  // empty array = roster not loaded yet (return nothing until it loads)
  const filteredScores = useMemo(() => {
    if (rosterPlayerIds === undefined) return scores;
    if (rosterPlayerIds.length === 0) return [];
    const rosterSet = new Set(rosterPlayerIds);
    return scores.filter(s => rosterSet.has(s.player_id) && s.team_id);
  }, [scores, rosterPlayerIds]);

  // Aggregate per-player
  const playerScores: Map<string, PlayerScoreSummary> = useMemo(() => {
    const map = new Map<string, PlayerScoreSummary>();
    for (const s of filteredScores) {
      const existing = map.get(s.player_id);
      if (existing) {
        existing.trash_points += Number(s.trash_points);
        existing.anti_points += Number(s.anti_points);
        existing.total_points += Number(s.total_points);
        existing.game_dates += 1;
      } else {
        map.set(s.player_id, {
          player_id: s.player_id,
          trash_points: Number(s.trash_points),
          anti_points: Number(s.anti_points),
          total_points: Number(s.total_points),
          game_dates: 1,
        });
      }
    }
    return map;
  }, [filteredScores]);

  // Team total
  const teamTotal = useMemo(() => {
    let total = 0;
    for (const s of playerScores.values()) {
      total += s.total_points;
    }
    return total;
  }, [playerScores]);

  return {
    scores: filteredScores,
    playerScores,
    teamTotal,
    loading,
    refetch: () => fetchScores(true),
  };
}
